<template>
    <component
        :is="numeric ? 'ol' : 'ul'"
        v-if="!pending && chapters && chapters.length"
        class="guideline-chapters"
    >
        <li
            v-for="(chapter, index) in chapters"
            :key="index"
            :data-active="`/${chapter.uri}` === $route.path || index === 0 && ($route.name === 'guideline-slug' || $route.name === 'guideline-summary')"
            class="guideline-chapters__item"
        >
            <a
                v-if="chapter.uri && chapter.title && chapter.isAnchor"
                class="guideline-chapters__title"
                :href="chapter.uri"
                @click.prevent="goToAnchor(chapter.uri)"
            >
                {{ titleCase(chapter.title) }}
            </a>

            <nuxt-link
                v-else-if="chapter.uri && chapter.title"
                :to="`/${chapter.uri}`"
            >
                {{ titleCase(chapter.title) }}
            </nuxt-link>

            <BasePill
                v-if="getHighlightCountBySlug(chapter.slug)"
                element="span"
                class="pill--result"
            >
                <template v-if="getHighlightCountBySlug(chapter.slug) > 1">
                    {{ getHighlightCountBySlug(chapter.slug) }} results
                </template>
                <template v-else>
                    1 result
                </template>
            </BasePill>
        </li>
    </component>

    <content-placeholders
        v-else-if="pending"
        class="guideline-chapters"
    >
        <div
            v-for="i in [1,2,3,4,5,6]"
            :key="i"
            class="guideline-chapters__item"
        >
            <content-placeholders-text :lines="1" />
        </div>
    </content-placeholders>
</template>


<script setup>
import { useGuidelineSearchStore } from '~/store/guideline-search';

const props = defineProps({
    pending: {
        type: Boolean,
        default: false,
        required: false
    },

    numeric: {
        type: Boolean,
        default: true,
        required: false
    },

    chapters: {
        type: Array,
        required: false,
        default: null
    },
    applyTitleCase: {
        type: Boolean,
        required: false,
        default: true
    }
});

const { chapters: storeChapters } = storeToRefs(useGuidelineSearchStore());

function titleCase(str) {
    if (typeof str !== 'string') {
        return str;
    }
    if (props.applyTitleCase) {
        return str.toLowerCase().split(' ').map(function(word) {
            if (!word) {
                return '';
            }
            return word.replace(word[0], word[0].toUpperCase());
        }).join(' ');
    } else {
        return str;
    }
}

function getHighlightCountBySlug(chapterSlug) {
    const chapterMeta = storeChapters.value?.find(chapter => chapter.slug === chapterSlug);

    if (!chapterMeta) {
        return;
    }

    return chapterMeta.highlightCount;
}

function goToAnchor(id) {
    const elPositionY = document.querySelector(`${id}`).offsetTop;
    const offset = -100;
    window.scrollTo({ behavior: 'smooth', top: elPositionY + offset });
}
</script>

<style lang="less">
.guideline-chapters {
    display: none;
    margin: 0;
    padding: .25rem 1rem;
    border-radius: var(--borderRadius-xs);
    background: var(--color-gray-200);

    @media @q-md-min {
        display: block;
    }

    @media @q-lg-min {
        padding: .5rem 1.75rem;
    }

    @media screen and ( min-width: @screen-md ) and ( min-height: 720px ) {
        position: sticky;
        top: 6rem;

        [data-searching='true'] & {
            top: 12.5rem;
        }
    }
}

ul.guideline-chapters {
    li:before {
        content: none;
        display: none;
    }

    li {
        padding-left: 0;
    }
}

.guideline-chapters__item {
    position: relative;

    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    margin: 0;
    padding: 0 0 0 2rem;
    line-height: 1;
    border-bottom: 1px solid var(--color-black-10);

    &:last-child {
        border-bottom: 0;
    }

    a, &:before {
        .typography-label-secondary;
        line-height: 1;
        text-decoration: none;
        opacity: .8;
    }

    .vue-content-placeholders-text,
    a {
        flex: 1 1 auto;
        display: inline;
        transition: opacity var(--transition-fast);
        line-height: 1.4;

        padding-top: 1rem;
        padding-bottom: 1rem;
    }

    .pill {
        white-space: nowrap;
        justify-self: flex-end;
        align-self: center;
    }

    &:active,
    &:hover {
        a, &:before {
            opacity: 1;
        }
    }

    &:active {
        a, &:before {
            color: var(--color-primary);
        }
    }

    &:before {
        top: auto;
        left: .625rem;
        line-height: 1.4;
        padding-top: 1rem;
        padding-bottom: 1rem;
    }

    @media @q-lg-min {
        &:before {
            left: .625rem;
        }
    }
}

.guideline-chapters__item[data-active='true'] {
    &, & a {
        color: var(--color-primary);
    }
}
</style>
